import cn from 'clsx';
import { useEffect, useState, JSX } from 'react';

import { RadioIcon } from '@/components/common';
import { getQaTestingProps } from '@/utils/qaTesting';

interface RadioGroupProps {
  readonly onSelectionChange?: (optionId: string) => void;
  readonly preSelectedValue?: string;
  readonly name: string;
  readonly options: InputRadioOption[];
}

export interface InputRadioOption {
  readonly id: string;
  readonly label: string | JSX.Element;
  readonly value: string;
  readonly disabled: boolean;
}

const styles = {
  container:
    'lg:px-[32px] lg:py-[20px] p-[20px] relative mt-xs first:mt-0 flex items-center md:ring-[1px] md:ring-silver-700 rounded-field bg-white min-h-[84px]',
  disabledContainer: 'bg-secondary opacity-50',
  containerFocus: 'hover:ring-silver-700 hover:!ring-[2px] focused-container',
  containerSelected: 'bg-secondary !ring-[#53090a] !ring-[2px]',
  input:
    'absolute w-[1px] h-[1px] p-[0] m-[-1px] border-0 opacity-0 pointer-events-none',
  label: 'w-full overflow-hidden inline-flex items-center',
  radioIcon: 'text-gray-900 order-last',
  radioIconSelected: 'text-primary order-last',
};

export function RadioGroup({
  onSelectionChange,
  preSelectedValue,
  name,
  options,
}: RadioGroupProps) {
  const [selected, setSelected] = useState<string>();

  useEffect(() => {
    if (options.length > 0) {
      setSelected(
        isPreSelectionValid(preSelectedValue, options)
          ? preSelectedValue
          : undefined,
      );
    }
  }, [options, preSelectedValue]);

  useEffect(() => {
    if (onSelectionChange && selected) {
      onSelectionChange(selected);
    }
  }, [selected]);

  return (
    <>
      {options.map((opt) => {
        return (
          <div
            key={`radio-${opt.id}`}
            className={cn(
              styles.container,
              opt.disabled === true && styles.disabledContainer,
              opt.disabled === false && styles.containerFocus,
              selected === opt.id && styles.containerSelected,
            )}
          >
            <input
              className={cn(
                styles.input,
                opt.disabled === false && 'cursor-pointer',
              )}
              onChange={() => setSelected(opt.id)}
              id={opt.id}
              type="radio"
              name={name}
              value={opt.value}
              disabled={opt.disabled === true}
            />
            <label
              className={cn(
                styles.label,
                opt.disabled === false && 'cursor-pointer',
              )}
              htmlFor={opt.id}
              {...getQaTestingProps(opt.id)}
            >
              {opt.label}
              <span
                className={
                  selected === opt.id
                    ? styles.radioIconSelected
                    : styles.radioIcon
                }
              >
                <RadioIcon
                  size={20}
                  selected={selected === opt.id}
                />
              </span>
            </label>
          </div>
        );
      })}
    </>
  );
}

function isPreSelectionValid(
  value: string | undefined,
  options: InputRadioOption[],
): boolean {
  return value !== undefined && options.map((o) => o.id).includes(value);
}
